/**
 * The guided-game intro: a short paged walkthrough of every basic — the goal and
 * royalty rule, how a turn works, deploying, the full army (standard pieces, the
 * fairy pieces, terrain), the ability cards, and how games end (mate + draws).
 * Piece and ability copy is pulled from the registries so this never drifts as
 * cards are added or changed. After the last page it hands off to a live guided
 * game where the coach takes over.
 */
import { createSignal, For, Show } from "solid-js";
import {
  abilityDef,
  pieceDef,
  type AbilityId,
  type PieceTypeId,
} from "../../core";
import { PieceIcon } from "./PieceIcon";
import { Icon } from "./Icon";

interface Slide {
  readonly title: string;
  readonly body?: string;
  /** Pieces to show as a labelled grid (art + name + registry description). */
  readonly pieces?: readonly PieceTypeId[];
  /** Ability cards to show as a labelled grid (glyph + name + blurb). */
  readonly abilities?: readonly AbilityId[];
}

const SLIDES: readonly Slide[] = [
  {
    title: "Welcome — chess, but you build the army",
    body: "You start with only a King and a Prince on the board. Everything else you deploy from a deck you build. Win by checkmating the enemy King.",
  },
  {
    title: "Royalty: the one new rule that matters most",
    body: "While your Prince is alive, your King cannot be checked or captured — it's untouchable. So the game is really two steps: first hunt down the enemy Prince to expose their King, then checkmate it. Guard your own Prince to keep your King safe.",
    pieces: ["king", "prince"],
  },
  {
    title: "Your turn",
    body: "At the start of every turn you automatically draw a card (up to a 7-card hand). Then you take exactly one action: deploy a card from your hand, or move a piece. Drawing is free — it never costs you your action.",
  },
  {
    title: "Deploying",
    body: "Tap a card in your hand, then a highlighted square in your own two home ranks, to place that piece. Terrain can go almost anywhere, and an Outpost lets you deploy further forward — more on those next.",
  },
  {
    title: "The classic army",
    body: "The five pieces you already know — they move exactly as in chess. Pawns promote to a Queen on the far rank. There's no castling or en passant.",
    pieces: ["pawn", "knight", "bishop", "rook", "queen"],
  },
  {
    title: "The fairy pieces",
    body: "New attackers with their own quirks. Tap-and-hold any piece in a game to see its moves again.",
    pieces: ["farmer", "lance", "outpost", "captain"],
  },
  {
    title: "Terrain — reshape the board",
    body: "Terrain never moves and can't be captured. It walls off squares for both sides. Hills and Walls each cover two squares (a single Mound covers one). To clear enemy terrain, Recall returns it to its owner — or Demolish destroys it outright.",
    pieces: ["hill", "valley", "ridge"],
  },
  {
    title: "Ability cards",
    body: "Not every card is a piece. Abilities bend the rules for a turn — playing one is your action. Royalty (King and Prince) can never be targeted by them.",
    abilities: ["dash", "sidestep", "recall", "resurrection", "demolish"],
  },
  {
    title: "Winning, and draws",
    body: "Checkmate the exposed King to win. A game is a draw by stalemate (no legal action, not in check), after a long stretch with no progress, or once the hard limit of 40 turns is reached — a counter in the top bar warns you as it nears. You can also offer your opponent a draw at any time.",
  },
  {
    title: "Ready? Let's play one together",
    body: "You'll play a gentle game against an easy computer. A coach in the corner will nudge you through deploying, taking the Prince, and landing the mate. Have fun.",
  },
];

function PieceCard(props: { type: PieceTypeId }) {
  const def = () => pieceDef(props.type);
  return (
    <div class="tut-card">
      <span class="tut-icon">
        <PieceIcon type={props.type} owner={0} />
      </span>
      <div class="tut-card-text">
        <strong>{def().name}</strong>
        <span>{def().description ?? `moves like a ${def().name.toLowerCase()}`}</span>
      </div>
    </div>
  );
}

function AbilityCard(props: { id: AbilityId }) {
  const def = () => abilityDef(props.id);
  return (
    <div class="tut-card">
      <span class="tut-icon glyph-ability">{def().glyph}</span>
      <div class="tut-card-text">
        <strong>{def().name}</strong>
        <span>{def().blurb}</span>
      </div>
    </div>
  );
}

export function TutorialIntro(props: { onStart: () => void; onExit: () => void }) {
  const [i, setI] = createSignal(0);
  const slide = () => SLIDES[i()]!;
  const last = () => i() === SLIDES.length - 1;
  const next = () => (last() ? props.onStart() : setI(i() + 1));
  const back = () => setI(Math.max(0, i() - 1));

  return (
    <div class="overlay">
      <div class="overlay-card overlay-card--tutorial">
        <div class="rules-head">
          <h2>How to play</h2>
          <button class="btn btn-ghost" onClick={props.onExit}>
            Skip
          </button>
        </div>

        <Show when={slide()} keyed>
          <div class="tut-body">
            <h3>{slide().title}</h3>
            <Show when={slide().body}>
              <p>{slide().body}</p>
            </Show>
            <Show when={slide().pieces}>
              <div class="tut-grid">
                <For each={slide().pieces}>{(t) => <PieceCard type={t} />}</For>
              </div>
            </Show>
            <Show when={slide().abilities}>
              <div class="tut-grid">
                <For each={slide().abilities}>{(a) => <AbilityCard id={a} />}</For>
              </div>
            </Show>
          </div>
        </Show>

        <div class="tut-foot">
          <div class="tut-dots" aria-hidden="true">
            <For each={SLIDES}>
              {(_, idx) => <span class="tut-dot" classList={{ on: idx() === i() }} />}
            </For>
          </div>
          <div class="field-row" style={{ gap: "8px" }}>
            <button class="btn btn-ghost" onClick={back} disabled={i() === 0}>
              <Icon name="chevron-left" /> Back
            </button>
            <button class="btn btn-primary" onClick={next}>
              {last() ? "Start playing" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
